/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import CardItemRow from '../../../layout/CardItemRow';
import {dlsColor} from '../../../theme/theme';
import {ClampedTextWithTooltip} from '../../assetcard/current/ClampedTextWithTooltip';

export interface InfoField {
  label: string;
  value: string;
  isEmpty?: boolean;
  clampLines?: number;
}

export interface InfoTwoColumnGridProps {
  fields: InfoField[];
  labelColor?: string;
}

interface InfoTwoColumnGridStyleProps {
  labelColor: string;
}

const useStyles = makeStyles(() => ({
  grid : {
    display    : 'flex',
    alignItems : 'flex-start',
    width      : '100%',
    gap        : '24px'
  },
  column : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    flex          : 1,
    minWidth      : 0,
    gap           : '16px'
  },
  // overrides CardItemRow's default horizontal (label-left, value-right) row
  // layout with the vertical label-over-value stack the Figma mockup calls
  // for - `!important` is needed since CardItemRow's own `row` class sets
  // these properties directly and class insertion order isn't guaranteed ...
  row : {
    flexDirection : 'column !important',
    alignItems    : 'flex-start !important',
    width         : '100%',
    gap           : '8px !important'
  },
  label : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : ({labelColor}: InfoTwoColumnGridStyleProps) => labelColor
  },
  value : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    wordBreak     : 'break-word',
    width         : '100%'
  },
  emptyValue : {
    color : dlsColor('grey3')
  },
  populatedValue : {
    color : dlsColor('grey2')
  }
}));

/**
 * InfoTwoColumnGrid lays a flat list of label/value `fields` out into two
 * flexible columns (192px each at the Additional Info section's 424px
 * reference width, separated by a 24px gap) - the first half of `fields`
 * fills the left column top-to-bottom, the remainder fills the right column,
 * matching the Figma mockup's two independent column stacks (e.g. Asset
 * Profile's left stack is only Name/Description while its right stack holds
 * Install Date/Type/Category). Callers must therefore order `fields` as
 * "left column top-to-bottom, then right column top-to-bottom", not in
 * left/right alternating (row-major) order.
 *
 * Each field renders through `CardItemRow` (`@ion/dls/src/layout/CardItemRow`)
 * for the row itself, with a custom `rowClass` that stacks its label above
 * its value (the mockup's layout) instead of `CardItemRow`'s own default
 * side-by-side row, and custom-styled label/value content passed as JSX so
 * the exact Roboto/Inter typography and per-field empty-state color
 * apply instead of `CardItemRow`'s own default text styles.
 *
 * `isEmpty` renders the value in the lighter `grey3` empty-state color (the
 * Description field's "There is no description listed." placeholder); a
 * literal fallback like Category's "N/A" is not "empty" and keeps the normal
 * `grey2` value color - only pass `isEmpty` for genuine placeholder prose.
 * `clampLines` (e.g. `3` for Description) wraps the value in
 * `ClampedTextWithTooltip` instead of rendering it as a plain span.
 *
 * ```typescript
 *
 *   import {InfoTwoColumnGrid} from
 *     '@ion/dls/src/components/assetdetails/current/InfoTwoColumnGrid';
 *   ...
 *
 *   <InfoTwoColumnGrid fields={[
 *     {label: 'Name', value: 'Vehicle 13 Vision'},
 *     {
 *       label: 'Description',
 *       value: 'There is no description listed.',
 *       isEmpty: true,
 *       clampLines: 3
 *     },
 *     {label: 'Install Date', value: 'Date format'},
 *     {label: 'Type', value: 'Vehicle'},
 *     {label: 'Category', value: 'N/A'}
 *   ]} />
 *
 * ```
 *
 */
export const InfoTwoColumnGrid = ({
  fields,
  labelColor = dlsColor('grey3')
}: InfoTwoColumnGridProps) => {
  const classes           = useStyles({labelColor});
  const leftCount         = Math.floor(fields.length / 2);
  const leftFields        = fields.slice(0, leftCount);
  const rightFields       = fields.slice(leftCount);
  const valueWrapperStyle = {width: '100%'};

  function renderField(field: InfoField) {
    const valueClassName = `${classes.value} ${field.isEmpty ? classes.emptyValue : classes.populatedValue}`;
    return (
      <CardItemRow key={field.label}
        rowClass={classes.row}
        isBottomMargin={false}
        isCenterAligned={false}
        stylesRight={valueWrapperStyle}
        label={
          <span className={classes.label}>
            {field.label}
          </span>
        }
        value={
          field.clampLines ? (
            <ClampedTextWithTooltip className={valueClassName}
              lines={field.clampLines}>
              {field.value}
            </ClampedTextWithTooltip>
          ) : (
            <span className={valueClassName}>
              {field.value}
            </span>
          )
        } />
    );
  }

  return (
    <div className={classes.grid}>
      <div className={classes.column}>
        {leftFields.map(renderField)}
      </div>
      <div className={classes.column}>
        {rightFields.map(renderField)}
      </div>
    </div>
  );
};

export default React.memo(InfoTwoColumnGrid);
